import React, { Component } from 'react';
import { IntlProvider } from 'react-intl';
import moment from 'moment';
import TranslateService from '../services/TranslateService';

import '@formatjs/intl-pluralrules/polyfill';
import '@formatjs/intl-pluralrules/locale-data/en';
import '@formatjs/intl-pluralrules/locale-data/fr';
import '@formatjs/intl-pluralrules/locale-data/nl';

import '@formatjs/intl-relativetimeformat/polyfill';
import '@formatjs/intl-relativetimeformat/locale-data/en';
import '@formatjs/intl-relativetimeformat/locale-data/fr';
import '@formatjs/intl-relativetimeformat/locale-data/nl';

require('moment/locale/fr');
require('moment/locale/nl-be');

// Parameters in angularjs are surounded by {{param}}. In react it's surounded by single {param}. So we change the double braces to single braces.
const changeDoubleBracesToSingle = value => {
  if (!value) return value;
  if (value.indexOf('{') < 0 || value.indexOf('}') < 0) return value; // Check if there are braces, this is faster than doing the replace regex on all values.
  return value.replace(/\{\{\s*([^}]+)\s*\}\}/g, '{$1}');
};

export default function withIntl(WrappedComponent) {
  return class extends Component {
    state = {
      messages: null,
      userCulture: null,
      currencyCode: null,
      version: 0,
    };

    initTranslations = () => {
      const { $translate } = this.props;

      const locale = $translate.use();
      const translations = $translate.getTranslationTable(locale);
      if (translations) {
        this.setState(() => ({
          messages: Object.keys(translations).reduce((o, key) => {
            // eslint-disable-next-line no-param-reassign
            o[key] = changeDoubleBracesToSingle(translations[key]); // See comment above function
            return o;
          }, {}),
          version: TranslateService.getVersion(),
        }));
      }
    };

    handleReload = () => {
      this.initTranslations();
    };

    componentDidMount() {
      const { $translate, dataCache } = this.props;

      if (dataCache) {
        dataCache.ready().then(() => {
          const { userLanguage = 'nl', userCulture } = dataCache.getArray('employee');
          const languages = dataCache.getArray('language');
          const lang = languages.find(x => x.code === userLanguage);
          const culture = (lang && lang.culture) || userCulture;
          if (!culture) {
            console.warn('User culture was not found in datacache');
          }
          const { currencyCode } = dataCache.getDefaultCompany();
          this.setState(() => ({
            userCulture: culture,
            currencyCode,
          }));
        });
      } else {
        console.warn('dataCache missing in WithIntl.jsx component');
      }

      if ($translate) {
        if ($translate.isReady()) {
          // Already loaded
          this.initTranslations();
        } else {
          // Still loading translations, wait for it to finish and then init translations
          $translate.onReady(() => {
            this.initTranslations();
          });
        }
      }
      TranslateService.onReload(this.handleReload);
    }

    componentWillUnmount() {
      TranslateService.offReload(this.handleReload);
    }

    handleError = error => {
      if (process.env.NODE_ENV !== 'production') {
        if (
          typeof error === 'string' &&
          error.indexOf('Missing message') >= 0 &&
          error.indexOf('using default message as fallback') >= 0
        )
          return;
        // console.warn(error);
      }
    };

    render() {
      const { $translate, ...passThroughProps } = this.props;
      const { messages, userCulture, currencyCode, version } = this.state;
      if (!$translate) {
        console.warn('$translate missing');
        return <WrappedComponent {...passThroughProps} />;
      }

      moment.locale(userCulture);
      const formats = {
        number: {
          currency: {
            style: 'currency',
            currency: currencyCode,
            currencyDisplay: 'symbol',
          },
        },
      };

      if (!messages || !userCulture) return null;

      return (
        <IntlProvider
          key={`${userCulture}-${version}`}
          locale={userCulture}
          messages={messages}
          onError={this.handleError}
          formats={formats}
          defaultFormats={formats}
          textComponent="span"
        >
          <WrappedComponent {...passThroughProps} />
        </IntlProvider>
      );
    }
  };
}
